'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import { supabase } from 'src/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { CHROME_EXTENSION } from 'src/constants/extension';

interface ExtensionStatus {
  isInstalled: boolean;
  isChecking: boolean;
  error: string | null;
}

export function useExtensionAuth() {
  const { user } = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [extensionStatus, setExtensionStatus] = useState<ExtensionStatus>({
    isInstalled: false,
    isChecking: true,
    error: null
  });

  // Manage session state directly with Supabase
  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setSession(session);
          setIsSessionLoading(false);
        }
      } catch {
        if (mounted) {
          setIsSessionLoading(false);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (mounted) {
          setSession(session);
          setIsSessionLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Get the extension ID from constants
  const getExtensionId = useCallback(() => {
    return CHROME_EXTENSION.ID;
  }, []);

  // Check if extension is installed
  const checkExtensionInstalled = useCallback(async (): Promise<boolean> => {
    try {
      const extensionId = getExtensionId();
      if (!extensionId) {
        // No extension ID configured
        return false;
      }

      return new Promise((resolve) => {
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
          resolve(false);
          return;
        }

        // Check if we're in a Chrome/Chromium browser
        if (!window.chrome) {
          // Not a Chrome/Chromium browser
          resolve(false);
          return;
        }

        // If chrome.runtime doesn't exist, the extension is definitely not installed
        if (!window.chrome.runtime?.sendMessage) {
          // chrome.runtime not available - extension not installed
          resolve(false);
          return;
        }

        // Set up timeout first
        const timeoutId = setTimeout(() => resolve(false), CHROME_EXTENSION.TIMEOUTS.STATUS_CHECK);

        // Send message to extension to check if it's installed
        try {
          const chromeRuntime = window.chrome?.runtime;
          if (!chromeRuntime?.sendMessage) {
            clearTimeout(timeoutId);
            resolve(false);
            return;
          }

          // Sending message to extension

          chromeRuntime.sendMessage(
            extensionId,
            { type: 'CHECK_EXTENSION_STATUS' },
            (response) => {
              clearTimeout(timeoutId);
              
              const lastError = chromeRuntime.lastError;
              // Check response
              
              if (lastError) {
                resolve(false);
              } else {
                resolve(response?.success === true);
              }
            }
          );
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Extension check error:', error);
          }
          clearTimeout(timeoutId);
          resolve(false);
        }
      });
    } catch {
      return false;
    }
  }, [getExtensionId]);

  // Share session tokens with extension
  const shareTokensWithExtension = useCallback(async (): Promise<boolean> => {
    if (!session?.access_token || !user) {
      return false;
    }

    try {
      const extensionId = getExtensionId();
      if (!extensionId) {
        return false;
      }

      // Check if we're in a browser environment with Chrome runtime
      if (typeof window === 'undefined' || !window.chrome?.runtime?.sendMessage) {
        return false;
      }

      const payload = {
        accessToken: session.access_token,
        refreshToken: session.refresh_token || '',
        expiresAt: session.expires_at ? session.expires_at * 1000 : Date.now() + 3600000, // Convert to milliseconds
        user: user
      };

      return new Promise((resolve) => {
        // Set up timeout first
        const timeoutId = setTimeout(() => resolve(false), CHROME_EXTENSION.TIMEOUTS.TOKEN_SHARE);

        try {
          const chromeRuntime = window.chrome?.runtime;
          if (!chromeRuntime?.sendMessage) {
            clearTimeout(timeoutId);
            resolve(false);
            return;
          }

          chromeRuntime.sendMessage(
            extensionId,
            { 
              type: 'SHARE_SESSION_TOKEN',
              payload 
            },
            (response) => {
              clearTimeout(timeoutId);
              
              const lastError = chromeRuntime.lastError;
              if (lastError) {
                resolve(false);
              } else {
                resolve(response?.success === true);
              }
            }
          );
        } catch {
          clearTimeout(timeoutId);
          resolve(false);
        }
      });
    } catch {
      return false;
    }
  }, [session, user, getExtensionId]);

  // Check extension status on mount and when session changes
  useEffect(() => {
    let isMounted = true;

    const checkExtension = async () => {
      if (!isMounted) return;
      
      setExtensionStatus(prev => ({ ...prev, isChecking: true, error: null }));

      try {
        const isInstalled = await checkExtensionInstalled();
        
        if (!isMounted) return;
        
        setExtensionStatus({
          isInstalled,
          isChecking: false,
          error: null
        });

        // If extension is installed and we have a session, share tokens
        if (isInstalled && session?.access_token && !isSessionLoading) {
          await shareTokensWithExtension();
        }
      } catch (error) {
        if (!isMounted) return;
        
        setExtensionStatus({
          isInstalled: false,
          isChecking: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    };

    // Only check if we're in a browser environment and session is not loading
    if (typeof window !== 'undefined' && !isSessionLoading) {
      checkExtension();
    }

    return () => {
      isMounted = false;
    };
  }, [session?.access_token, user?.id, isSessionLoading, checkExtensionInstalled, shareTokensWithExtension]);

  return {
    extensionStatus,
    shareTokensWithExtension
  };
}